import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Pencil, Save, X, Sparkles } from "lucide-react";
import { api, imgSrc, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const since = (s) => new Date(s).toLocaleDateString("de-DE", { month: "2-digit", year: "numeric" });
export const defaultNote = (name) => `Hallo ${name?.split(" ")[0] || "du Liebe:r"}, danke, dass du unsere Community mit so viel Herz unterstützt! Ohne Menschen wie dich wäre Itzcouple nicht das, was es ist. Wir sind unendlich dankbar, dass es dich gibt.`;

export function DankeBanner({ p }) {
  return (
    <div className="relative overflow-hidden rounded-2xl sp-banner px-4 py-3 flex items-center gap-3" data-testid="danke-banner-since-date">
      <span className="shrink-0 w-10 h-10 rounded-full bg-white shadow-sm ring-1 ring-amber-200 flex items-center justify-center"><Heart className="w-5 h-5 text-rose-500 hh-beat" fill="currentColor" /></span>
      <p className="min-w-0 text-sm sm:text-base font-bold text-[#5b1f35] leading-snug">
        {p.supporter_since ? <>Unterstützt unsere Community seit <span className="sp-gold-text font-black">{since(p.supporter_since)}</span> ❤️</> : <>Unterstützt unsere Community ❤️</>}
        <span className="block text-xs font-semibold text-[#9a5470]">Danke von Herzen – du hältst itzPlay für alle kostenlos.</span>
      </p>
      <Sparkles className="absolute right-3 top-2 w-4 h-4 text-amber-400 vip-twinkle" aria-hidden />
    </div>
  );
}

function useCreator() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/creator").then(({ data }) => setC(data)).catch(() => {}); }, []);
  return c;
}

function NoteEditor({ p, onSaved, onClose }) {
  const [text, setText] = useState(p.supporter_note || "");
  const save = async () => {
    try { const { data } = await api.put(`/admin/users/${p.user_id}/supporter-note`, { text }); onSaved(data.supporter_note); toast.success("Danke-Nachricht gespeichert ❤️"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="itzcouple-thanks-editor">
      <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 400))} rows={4} placeholder={defaultNote(p.name)} data-testid="itzcouple-thanks-input"
        className="w-full rounded-2xl bg-white ring-1 ring-pink-200 px-4 py-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-rose-400" />
      <p className="text-[11px] text-[#9a5470]">Leer lassen = Standard-Danke. {text.length}/400</p>
      <div className="flex gap-2">
        <button onClick={save} data-testid="itzcouple-thanks-save" className="h-9 px-4 rounded-full pink-gradient text-white text-xs font-extrabold inline-flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Speichern</button>
        <button onClick={onClose} data-testid="itzcouple-thanks-cancel" className="h-9 px-4 rounded-full bg-white ring-1 ring-pink-200 text-rose-700 text-xs font-extrabold inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Abbrechen</button>
      </div>
    </div>
  );
}

export function ItzcoupleThanks({ p }) {
  const { user } = useAuth();
  const c = useCreator();
  const [note, setNote] = useState(p.supporter_note || "");
  const [edit, setEdit] = useState(false);
  const admin = user?.role === "admin";
  return (
    <section className="relative overflow-hidden rounded-3xl sp-quote p-5 sm:p-7" data-testid="itzcouple-thanks-card">
      <Heart className="absolute -right-6 -bottom-8 w-40 h-40 text-rose-200/40 rotate-12" fill="currentColor" strokeWidth={0} aria-hidden />
      <div className="relative flex items-center gap-3">
        <span className="relative shrink-0 w-12 h-12 rounded-full p-[2px] bg-gradient-to-br from-pink-400 via-rose-400 to-amber-300">
          <span className="block w-full h-full rounded-full overflow-hidden bg-white">{c?.picture ? <img src={imgSrc(c.picture)} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center font-display font-black text-rose-500">IC</span>}</span>
          <Heart className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-rose-500 drop-shadow" fill="currentColor" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-rose-500">Persönliches Danke</p>
          <h2 className="font-display text-lg sm:text-xl font-black text-[#3d1728] leading-tight">Von Itzcouple an {p.name?.split(" ")[0]}</h2>
        </div>
        {admin && !edit && <button onClick={() => setEdit(true)} data-testid="itzcouple-thanks-edit" aria-label="Danke-Nachricht bearbeiten" className="shrink-0 w-9 h-9 rounded-full bg-white ring-1 ring-pink-200 text-rose-600 flex items-center justify-center hover:bg-rose-50"><Pencil className="w-4 h-4" /></button>}
      </div>
      {edit ? <NoteEditor p={{ ...p, supporter_note: note }} onSaved={(t) => { setNote(t); setEdit(false); }} onClose={() => setEdit(false)} /> : <>
        <blockquote className="relative mt-4 font-hand text-[22px] sm:text-[26px] leading-snug text-[#5b1f35] whitespace-pre-line" data-testid="itzcouple-thanks-quote">„{note || defaultNote(p.name)}“</blockquote>
        <p className="relative mt-3 font-hand text-2xl text-rose-600" data-testid="itzcouple-signature">Saskia & Stefan ❤️</p>
      </>}
    </section>
  );
}
